import { useCallback, useEffect, useRef, useState } from 'react';
import { sanitizeAutocompleteValue, useAutocompleteOptions, } from './useAutocompleteOptions';
import { useAutocompleteSelection } from './useAutocompleteSelection';
import { useAutocompleteNavigation } from './useAutocompleteNavigation';
import { useTouchSuggestion } from './useTouchSuggestion';
export const useAutocomplete = ({ type, value, onChange, onBlur, }) => {
    const rootRef = useRef(null);
    const [inputValue, setInputValue] = useState(sanitizeAutocompleteValue(value));
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const { suggestions, isLoading, setDebouncedSearchValue } = useAutocompleteOptions({
        type,
        value: inputValue,
        isOpen,
    });
    const findMatchedSuggestion = useCallback((nextValue) => suggestions.find((suggestion) => suggestion.name.toLowerCase() ===
        nextValue.trim().toLowerCase()), [suggestions]);
    const { selectItem, handleSuggestionMouseDown, handleSuggestionClick } = useAutocompleteSelection({
        onChange,
        setInputValue,
        setDebouncedSearchValue,
        setIsOpen,
        setActiveIndex,
    });
    const { handleKeyDown, handleDropdownMouseDown } = useAutocompleteNavigation({
        isOpen,
        suggestions,
        activeIndex,
        setIsOpen,
        setActiveIndex,
        selectItem,
    });
    const { handleSuggestionTouchStart, handleSuggestionTouchMove, handleSuggestionTouchEnd, handleSuggestionTouchCancel, } = useTouchSuggestion(selectItem);
    const handleInputChange = (newValue) => {
        const sanitizedValue = sanitizeAutocompleteValue(newValue);
        setInputValue(sanitizedValue);
        setIsOpen(true);
        setActiveIndex(-1);
        const matched = findMatchedSuggestion(sanitizedValue);
        onChange(sanitizedValue, matched?.code || null);
    };
    const handleFocus = () => {
        setIsOpen(true);
    };
    const handleBlur = () => {
        setTimeout(() => {
            if (!rootRef.current?.contains(document.activeElement)) {
                setIsOpen(false);
                const finalInputValue = inputValue.trim();
                const matched = findMatchedSuggestion(finalInputValue);
                const finalValue = matched?.name || finalInputValue;
                const finalCode = matched?.code || null;
                setInputValue(finalValue);
                setDebouncedSearchValue(finalValue);
                onChange(finalValue, finalCode);
                onBlur();
            }
        }, 150);
    };
    useEffect(() => {
        const sanitizedValue = sanitizeAutocompleteValue(value);
        if (sanitizedValue !== inputValue) {
            setInputValue(sanitizedValue);
            setDebouncedSearchValue(sanitizedValue);
        }
    }, [value]);
    return {
        rootRef,
        inputValue,
        suggestions: suggestions,
        isOpen,
        isLoading,
        activeIndex,
        handleInputChange,
        handleFocus,
        handleBlur,
        handleKeyDown,
        handleDropdownMouseDown,
        handleSuggestionMouseDown,
        handleSuggestionClick,
        handleSuggestionTouchStart,
        handleSuggestionTouchMove,
        handleSuggestionTouchEnd,
        handleSuggestionTouchCancel,
    };
};
